import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Divider,
  Button,
  TextField,
  Stack,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Menu,
  MenuItem,
  Chip,
} from "@mui/material";
import { Search, MoreVert, ArrowUpward, ArrowDownward, FilterList } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../supabaseClient"; // Assuming you have this setup
import TemplateView from "./TemplateView";
import CopyTemplateDialog from "./CopyTemplateDialog";
import { templateDisplayTitle, isBuiltInHomePracticeTemplate } from "../../MedicalScribe/homeworkPracticeTemplate";
import TemplateLayout from "./TemplateLayout";
import { useSelector, shallowEqual } from "react-redux";
import useAlert from "../../../alert/useAlert";
import mammoth from "mammoth";

const TemplateList = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "updated_at", direction: "desc" });
  const [creatorFilter, setCreatorFilter] = useState('all');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [anchorEl, setAnchorEl] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [seletedTemplateID, setSelectedTemplateID] = useState(null);
  const [reportContent, setReportContent] = useState("");
  const [fileName, setFileName] = useState("");
  const [copySource, setCopySource] = useState(null);
  const [view, setView] = useState('list');
  const [uploadLoading, setUploadLoading] = useState(false);
  const { userDetails } = useSelector((state) => ({userDetails: state.globalState.userDetails}), shallowEqual);
  const currentOrg = useSelector((state) => state.globalState.currentOrg, shallowEqual);
  const { setAlert } = useAlert();

  useEffect(() => {
    fetchTemplates();
  }, [sortConfig, currentOrg?.id]);

  useEffect(() => {
    setView(current => current === 'preview' ? 'list' : current);
  }, [currentOrg?.id, userDetails.id]);

  const fetchTemplates = async () => {
    try {
      // Include templates from other members of the current org
      let memberIds = [userDetails.id];
      if (currentOrg?.id) {
        const { data: members, error: membersError } = await supabase
          .from('team_users')
          .select('user_id')
          .eq('team_id', currentOrg.id);
        if (!membersError && members?.length) {
          memberIds = [...new Set([userDetails.id, ...members.map((m) => m.user_id)])];
        }
      }

      const { data, error } = await supabase
        .from('templates')
        .select('*')
        .in('userid', memberIds)
        .order(sortConfig.key, { ascending: sortConfig.direction === 'asc' });

      if (error) throw error;

      // Every member gets their own seeded copies of the base templates —
      // only show the current user's to avoid listing duplicates
      const visibleTemplates = data.filter(
        (template) => template.userid === userDetails.id || !template.is_base_template
      );

      const decryptionPromises = visibleTemplates.map(async (template) => {
        const [titleResult, contentResult, createdByResult] = await Promise.all([
          supabase.rpc('decrypt_text', { encrypted_data: template.title }),
          supabase.rpc('decrypt_text', { encrypted_data: template.content }),
          supabase.rpc('decrypt_text', { encrypted_data: template.created_by })
        ]);
        if(titleResult.error || contentResult.error || createdByResult.error) {
          return template;
        }
        template.title = titleResult.data;
        template.content = contentResult.data;
        template.created_by = createdByResult.data;

        return { ...template, is_base_template: template.is_base_template || isBuiltInHomePracticeTemplate(template) };
      });

      const decryptedTemplates = await Promise.all(decryptionPromises);
      setTemplates(decryptedTemplates.filter(template => template.userid === userDetails.id || !template.is_base_template));
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleSort = (key) => {
    // Re-fetch happens via the useEffect on sortConfig
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleMenuOpen = (event, template) => {
    setAnchorEl(event.currentTarget);
    setSelectedTemplate(template);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const updateDefaultReport = async (selectedTemplateid) => {
    try {
      if(selectedTemplate.default === 'report') {
        setAlert('This template is already set as default for reports', 'info');
        return;
      } 
      if(selectedTemplate.default === 'insights') {
        setAlert('This template is already set as default for patient insights', 'info');
        return;
      }
      const { error: updateError } = await supabase.from('templates').update({ default: 'report' }).eq('id', selectedTemplateid);
  
      if (updateError) {
        setAlert('Failed to update template settings', 'error');
        return;
      }
  
      // Get all other template IDs that were previously set as default report templates
      const otherTemplateIds = templates.filter(template => template.id !== selectedTemplateid && template.default === 'report').map(template => template.id);
  
      if (otherTemplateIds.length > 0) {
        const { error: resetError } = await supabase.from('templates').update({ default: null }).in('id', otherTemplateIds);
  
        if (resetError) {
          console.error('Error resetting other templates:', resetError);
          setAlert('Templates updated but there was an issue with resetting other templates', 'warning');
          return;
        }
      }
      setAlert('Succesfully set as the default report tempalte', 'success');
      fetchTemplates();
    } catch (error) {
      console.error('Unexpected error updating templates:', error);
      setAlert('An unexpected error occurred', 'error');
    }
  };

  const updateDefaultInsights = async (selectedTemplateid) => {
    try {
      if(selectedTemplate.default === 'insights') {
        setAlert('This template is already set as default for patient insights', 'info');
        return;
      } 
      if(selectedTemplate.default === 'report') {
        setAlert('This template is already set as default for reports', 'info');
        return;
      }
      const { error: updateError } = await supabase.from('templates').update({ default: 'insights' }).eq('id', selectedTemplateid);
  
      if (updateError) {
        setAlert('Failed to update template settings', 'error');
        return;
      }
  
      // Get all other template IDs that were previously set as default insights templates
      const otherTemplateIds = templates.filter(template => 
        template.id !== selectedTemplateid && template.default === 'insights'
      ).map(template => template.id);
  
      if (otherTemplateIds.length > 0) {
        const { error: resetError } = await supabase.from('templates').update({ default: null }).in('id', otherTemplateIds);
  
        if (resetError) {
          console.error('Error resetting other templates:', resetError);
          setAlert('Templates updated but there was an issue with resetting other templates', 'warning');
          return;
        }
      }
      setAlert('Successfully set as the default patient insights template', 'success');
      fetchTemplates(); 
    } catch (error) {
      console.error('Unexpected error updating templates:', error);
      setAlert('An unexpected error occurred', 'error');
    }
  };

const removeDefault = async (selectedTemplateid) => {
    try {
      if(selectedTemplate.default === null) {
        setAlert('This template is not set as default for any report or insights', 'info');
        return;
      } 
      const { error: updateError } = await supabase.from('templates').update({ default: null }).eq('id', selectedTemplateid);
  
      if (updateError) {
        setAlert('Failed to update template settings', 'error');
        return;
      }
  
      setAlert('Successfully removed default setting from the template', 'success');
      fetchTemplates(); 
    } catch (error) {
      console.error('Unexpected error updating templates:', error);
      setAlert('An unexpected error occurred', 'error');
    }
}

const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return;
    try {
      const { error } = await supabase.from('templates').delete().eq('id', selectedTemplate.id);
      if (error) {
        setAlert('Failed to delete template', 'error');
        return;
      }
      setAlert('Template deleted successfully', 'success');
      fetchTemplates();
      handleMenuClose();
    } catch (error) {
      console.error('Error deleting template:', error);
      setAlert('An unexpected error occurred while deleting the template', 'error');
    } 
  };


  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    const allowedTypes = [
      'text/plain',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/pdf'
    ];

    if (!allowedTypes.includes(file.type)) {
      setAlert('Please upload a valid text (.txt), Word (.docx), or PDF (.pdf) file', 'error');
      event.target.value = ''; // Reset file input
      return;
    }

    setUploadLoading(true);
    
    try {
      let content = '';

      if (file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
        const arrayBuffer = await file.arrayBuffer();
        const convertedContent = await mammoth.convertToHtml({ arrayBuffer });
        content = convertedContent.value;
      }
      else if (file.type === "text/plain") {
        const convertedContent = await file.text();
        content = `<pre>${convertedContent}</pre>`
      } 
      else if (file.type === "application/pdf") {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch(`${process.env.REACT_APP_MAIN_BACKEND_URL}/convert`, {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          await fetch(`${process.env.REACT_APP_MAIN_BACKEND_URL}/clear_uploads`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          });

          const docxBlob = await response.blob();
          const arrayBuffer = await docxBlob.arrayBuffer();
          const convertedContent = await mammoth.convertToHtml({ arrayBuffer });
          content = convertedContent.value;
        } else {
          setAlert('Failed to process PDF file', 'error');
          return;
        }
      }

      setReportContent(content);
      const fileNameWithoutExt = file.name.split('.').slice(0, -1).join('.');
      setFileName(fileNameWithoutExt);
      setSelectedTemplateID(null);
      setView('New');
    } catch (error) {
      console.error('Error processing file:', error);
      setAlert('Failed to process the uploaded file', 'error');
    } finally {
      setUploadLoading(false);
      event.target.value = ''; // Reset file input
    }
  };


  const handleAction = async (action) => {
    if (!selectedTemplate) return;
    
    switch (action) {
      case 'setDefaultReport':
        updateDefaultReport(selectedTemplate.id);
        break;
      case 'setDefaultInsights':
        updateDefaultInsights(selectedTemplate.id);
        break;
      case 'removeDefault':
        removeDefault(selectedTemplate.id);
        break;
      case 'copy':
        setCopySource(selectedTemplate);
        break;
      case 'view':
        setReportContent('');
        setFileName('');
        setSelectedTemplateID(selectedTemplate.id);
        setView('preview');
        break;
      case 'edit':
        if (selectedTemplate.userid !== userDetails.id || selectedTemplate.is_base_template) break;
        setSelectedTemplateID(selectedTemplate.id);
        setView('edit');
        break;
      case 'delete':
        handleDeleteTemplate();
        break;
      default:
        break;
    }
    
    handleMenuClose();
  };

  const isMine = (template) => template.userid === userDetails.id;
  const creatorLabel = (template) => (isMine(template) ? 'Me' : template.created_by || 'Unknown');

  // Unique creator names of other org members, for the Created By filter
  const otherCreators = [...new Set(templates.filter(t => !isMine(t)).map(creatorLabel))].sort();

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = [template.title, templateDisplayTitle(template.title)]
      .some(title => title?.toLowerCase().includes(searchText.toLowerCase()));
    const matchesCreator = creatorFilter === 'all' || creatorLabel(template) === creatorFilter;
    return matchesSearch && matchesCreator;
  });

  return (
    <div>
      {copySource && <CopyTemplateDialog source={copySource} userDetails={userDetails} orgId={currentOrg?.id}
        onClose={() => setCopySource(null)} onCopied={copy => {
          setTemplates(items => [copy, ...items]);
          setSearchText(''); setCreatorFilter('all'); setCopySource(null);
          setAlert('Template copied. You can now edit your copy.', 'success');
        }} />}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h5" sx={{ marginBottom: 3 }}>
          Template Builder
        </Typography>
        <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={2}>
          <Button 
            variant="contained" 
            onClick={() => setView('layout')} 
            sx={{ borderRadius: 30, backgroundColor: '#F8F9FA', padding: '5px 10px', color: "#29C0BB", cursor: 'pointer'}}
          >
            Layout Settings
          </Button>
          
          <input
            type="file"
            id="file-upload"
            style={{ display: 'none' }}
            accept=".txt,.docx,.pdf"
            onChange={handleFileUpload}
          />
          <Button 
            variant="contained" 
            onClick={() => document.getElementById('file-upload').click()}
            disabled={uploadLoading}
            sx={{ borderRadius: 30, backgroundColor: '#29C0BB', padding: '5px 10px', color: "#FFFFFF", cursor: 'pointer'}}
          >
            {uploadLoading ? 'Processing...' : 'Upload Report'}
          </Button>

          <Button 
            variant="contained" 
            onClick={() => { setSelectedTemplateID(null); setView('New')}} 
            sx={{ borderRadius: 30, backgroundColor: '#29C0BB', padding: '5px 10px', color: "#FFFFFF", cursor: 'pointer'}}
          >
            New
          </Button>
        </Stack>
      </Box>

      {
        view === 'list' ? (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", my: 2 }}>
              <Typography variant="h6">
                Saved Templates
              </Typography>
              <TextField
                placeholder="Search templates..."
                size="small"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: '300px' }}
              />
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell onClick={() => handleSort('updated_at')} sx={{ cursor: 'pointer' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Last Updated
                        {sortConfig.key === 'updated_at' && (
                          sortConfig.direction === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>Template</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Created By
                        <IconButton
                          size="small"
                          onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                          sx={{ ml: 0.5, color: creatorFilter !== 'all' ? '#29C0BB' : 'inherit' }}
                        >
                          <FilterList fontSize="small" />
                        </IconButton>
                        {creatorFilter !== 'all' && (
                          <Chip
                            label={creatorFilter}
                            size="small"
                            onDelete={() => setCreatorFilter('all')}
                            sx={{ ml: 0.5, height: '20px', '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
                          />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTemplates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell>{new Date(template.updated_at).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          flexWrap: 'nowrap',
                        }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {templateDisplayTitle(template.title)}
                          </Typography>
                          {template.default && (
                            <Chip 
                              label={template.default === 'report' ? 'Default Report' : template.default === 'insights' ? 'Default Insights' : ''}
                              size="small" 
                              sx={{ 
                                ml: 1,
                                backgroundColor: '#29C0BB', 
                                color: 'white',
                                height: '20px', 
                                '& .MuiChip-label': {
                                  px: 1,     
                                  py: 0.25,       
                                  fontSize: '0.7rem' 
                                }
                              }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <IconButton aria-label={`Actions for ${templateDisplayTitle(template.title)}`} onClick={(e) => handleMenuOpen(e, template)}>
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        {isMine(template) ? (
                          <Chip
                            label="Me"
                            size="small"
                            sx={{
                              backgroundColor: '#29C0BB',
                              color: 'white',
                              height: '20px',
                              '& .MuiChip-label': { px: 1, py: 0.25, fontSize: '0.7rem' }
                            }}
                          />
                        ) : (
                          template.created_by || 'Unknown'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredTemplates.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">No templates found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Menu
              anchorEl={filterAnchorEl}
              open={Boolean(filterAnchorEl)}
              onClose={() => setFilterAnchorEl(null)}
            >
              {['all', 'Me', ...otherCreators].map((option) => (
                <MenuItem
                  key={option}
                  selected={creatorFilter === option}
                  onClick={() => { setCreatorFilter(option); setFilterAnchorEl(null); }}
                >
                  {option === 'all' ? 'All' : option}
                </MenuItem>
              ))}
            </Menu>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {selectedTemplate?.userid === userDetails.id && (
                <>
                  <MenuItem onClick={() => handleAction('setDefaultReport')}>
                    Set as Default Report
                  </MenuItem>
                  {/* <MenuItem onClick={() => handleAction('setDefaultInsights')}>
                    Set as Default Patient Insights
                  </MenuItem> */}
                  <MenuItem onClick={() => handleAction('removeDefault')}>
                    Remove Default
                  </MenuItem>
                </>
              )}
              {selectedTemplate?.userid !== userDetails.id && (
                <MenuItem onClick={() => handleAction('view')}>View</MenuItem>
              )}
              <MenuItem onClick={() => handleAction(selectedTemplate?.userid === userDetails.id && !selectedTemplate?.is_base_template ? 'edit' : 'copy')}>
                {selectedTemplate?.userid === userDetails.id && !selectedTemplate?.is_base_template ? 'Edit' : 'Copy'}
              </MenuItem>
              {selectedTemplate?.userid === userDetails.id && (
                <MenuItem onClick={() => handleAction('delete')}>
                  Delete
                </MenuItem>
              )}
            </Menu>
          </>)
          : view === 'layout' ?
          <TemplateLayout 
            handleBack={() => {setView('list'); fetchTemplates();}}
            userDetails={userDetails}
          />
          :
          <TemplateView 
            key={`${seletedTemplateID || 'new'}-${view}`}
            readOnly={view === 'preview'}
            templateContent={reportContent ?? null}
            templateTitle={fileName ?? null}
            handleBack={() => {setView('list'); fetchTemplates(); setReportContent(""); setFileName("");}}
            templateId={seletedTemplateID}
            userDetails={userDetails}
            view="template"
          />
      }
    </div>
  );
};

export default TemplateList;
